/**
 * CountryGate — replaces the app shell with a friendly "coming soon" page
 * for visitors whose ISO country code is in the admin's blocked list.
 *
 * Per product direction, the screen MUST NOT reveal that the visitor's
 * country is restricted: no flag, no country name, no "blocked" wording.
 * Instead we show an apology, a bold "coming soon — wait for us" line,
 * and an email capture so the visitor can be notified at launch.
 *
 * Emails are written to Firestore collection `launch-subscribers`.
 * The admin route (/admin/blocked-countries) stays exempt so admins can
 * always unblock from a non-blocked region.
 */
import type { ReactNode, FormEvent } from "react";
import { useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { useVisitorCountry } from "@/lib/visitor-country";
import { useTheme } from "@/lib/theme-context";

const COPY = {
  ar: {
    oops: "أوبس!",
    sorry: "نأسف على ما تراه، صبرٌ جميل.",
    soon: "قادم قريباً — انتظرنا",
    hint: "أدخل بريدك الإلكتروني لتكون أول من يعلم متى وقت الإطلاق.",
    placeholder: "بريدك الإلكتروني",
    submit: "أعلمني",
    success: "تم! سنخبرك فور الإطلاق.",
    error: "تعذّر الحفظ، حاول مجدداً.",
    invalid: "الرجاء إدخال بريد صحيح.",
  },
  fr: {
    oops: "Oups !",
    sorry: "Désolé pour ce que vous voyez, un peu de patience.",
    soon: "Bientôt disponible — attendez-nous",
    hint: "Entrez votre email pour être le premier informé du lancement.",
    placeholder: "Votre email",
    submit: "Me prévenir",
    success: "C'est fait ! Nous vous préviendrons au lancement.",
    error: "Échec de l'enregistrement, réessayez.",
    invalid: "Veuillez entrer un email valide.",
  },
  en: {
    oops: "Oops!",
    sorry: "Sorry for what you see — please bear with us.",
    soon: "Coming soon — wait for us",
    hint: "Enter your email to be the first to know when we launch.",
    placeholder: "Your email",
    submit: "Notify me",
    success: "Done! We'll let you know at launch.",
    error: "Could not save, please try again.",
    invalid: "Please enter a valid email.",
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BLOCKED_COUNTRIES_KEY = "rawan:blocked-countries";

function readBlockedCountries(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(BLOCKED_COUNTRIES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function CountryGate({ children }: { children: ReactNode }) {
  const [codes, setCodes] = useState<string[]>(() => readBlockedCountries());
  const { country, loading } = useVisitorCountry();
  const { language } = useTheme();
  const router = useRouter();
  const [path, setPath] = useState<string>(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );
  useEffect(() => {
    const unsub = router.subscribe("onResolved", () => setPath(window.location.pathname));
    return unsub;
  }, [router]);

  useEffect(() => {
    const refresh = () => setCodes(readBlockedCountries());
    const onStorage = (e: StorageEvent) => {
      if (e.key === BLOCKED_COUNTRIES_KEY) refresh();
    };
    window.addEventListener("rawan:blocked-countries-changed", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("rawan:blocked-countries-changed", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "ok" | "err" | "invalid">("idle");

  const isAdminArea =
    path.startsWith("/admin/blocked-countries") ||
    path.startsWith("/dashboard") ||
    path.startsWith("/login");

  if (loading || !country || isAdminArea) return <>{children}</>;
  if (!codes.includes(country.toUpperCase())) return <>{children}</>;

  const t = COPY[language];

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      setStatus("invalid");
      return;
    }
    setStatus("saving");
    try {
      const [{ addDoc, collection, serverTimestamp }, { getDb }] = await Promise.all([
        import("firebase/firestore"),
        import("@/lib/firebase"),
      ]);
      const db = getDb();
      if (!db) throw new Error("no-db");
      await addDoc(collection(db, "launch-subscribers"), {
        email: value,
        language,
        createdAt: serverTimestamp(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
      setStatus("ok");
      setEmail("");
    } catch {
      setStatus("err");
    }
  };

  return (
    <div
      dir={language === "ar" ? "rtl" : "ltr"}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background text-foreground px-6 text-center"
    >
      <div className="w-full max-w-md">
        <h1 className="text-4xl font-extrabold tracking-tight">{t.oops}</h1>
        <p className="mt-3 text-base text-muted-foreground">{t.sorry}</p>
        <p className="mt-2 text-2xl font-extrabold text-primary">{t.soon}</p>

        <p className="mt-6 text-sm text-muted-foreground">{t.hint}</p>

        <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (status !== "idle") setStatus("idle"); }}
            placeholder={t.placeholder}
            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            dir="ltr"
          />
          <button
            type="submit"
            disabled={status === "saving"}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {t.submit}
          </button>
        </form>

        {status === "ok" && (
          <p className="mt-3 text-sm font-medium text-green-600 dark:text-green-400">{t.success}</p>
        )}
        {status === "err" && (
          <p className="mt-3 text-sm font-medium text-destructive">{t.error}</p>
        )}
        {status === "invalid" && (
          <p className="mt-3 text-sm font-medium text-destructive">{t.invalid}</p>
        )}
      </div>
    </div>
  );
}
